"use client";

import { useState } from "react";
import { User, Copy } from "lucide-react";
import GeneratorPage from "../GeneratorPage";
import { CATEGORIES } from "@/constants/categories";
import { Button, Input, Select } from "@/components/design-system";
import { generateNames, GeneratedName, namesetLabels } from "@/lib/nameGenerator";
import { useTranslations, useLocale } from "next-intl";
import { useFormField } from "@/hooks";
import { Controller } from "react-hook-form";
import { detectDefaultCountry } from "@/lib/utilts";
import SchemaScript from "@/components/common/SchemaScript";
import { Routes } from "@/constants/routes";
import { Locale } from "@/constants/locales";

export default function NamesGenerator() {
    const t = useTranslations("names");
    const locale = useLocale() as Locale;
    const [isGenerating, setIsGenerating] = useState(false);
    const [generated, setGenerated] = useState<GeneratedName[]>([]);
    const [copied, setCopied] = useState(false);
    const { control } = useFormField();

    // Get default country based on current locale
    const defaultCountry = detectDefaultCountry(locale);

    // Fallback form state for when react-hook-form is not available
    const [fallbackForm, setFallbackForm] = useState({
        gender: "any",
        nameset: defaultCountry.code,
        nameType: "full",
        count: 10,
    });

    const handleGenerate = (e: React.FormEvent, formData?: any) => {
        e.preventDefault();
        const data = formData || fallbackForm;

        setIsGenerating(true);
        setTimeout(() => {
            setGenerated(generateNames(data));
            setIsGenerating(false);
        }, 400); // Simulate async
    };

    const handleCopyAll = async () => {
        await navigator.clipboard.writeText(generated.map((n) => n.fullName).join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    // Namesets for select
    const namesetOptions = [
        { value: "any", label: t("form.namesetOptions.any") },
        ...Object.entries(namesetLabels).map(([code, name]) => ({
            value: code,
            label: name
        }))
    ];

    // Custom results renderer for names
    const customResultsRenderer = (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                {generated.length > 0 && (
                    <Button type="button" size="sm" variant="outline" onClick={handleCopyAll}>
                        {copied ? t("results.copyAllSuccess") : t("results.copyAll")}
                    </Button>
                )}
            </div>
            <div className="space-y-2">
                {generated.length === 0 ? (
                    <div className="bg-muted rounded-lg p-4 min-h-[60px]">
                        <span className="text-muted-foreground">{t("results.noResults")}</span>
                    </div>
                ) : (
                    generated.map((n) => (
                        <div key={n.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-500 transition-colors group">
                            <span className="font-medium text-gray-900">{n.fullName}</span>
                            <button
                                onClick={() => {
                                    navigator.clipboard.writeText(n.fullName);
                                    setCopied(true);
                                    setTimeout(() => setCopied(false), 1200);
                                }}
                                className="p-2 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={t("form.copyButton")}
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );

    // Form configuration with validation
    const formConfig = {
        values: {
            gender: "any",
            nameset: defaultCountry.code,
            nameType: "full",
            count: 10,
        },
        fields: [
            {
                key: "gender",
                label: t("form.gender"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "nameType",
                label: t("form.nameType"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "nameset",
                label: t("form.nameset"),
                type: "select" as const,
                validation: { required: true },
            },
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                validation: {
                    required: true,
                    min: 1,
                    max: 10,
                },
            },
        ],
        onChange: () => { }, // Not needed with react-hook-form
    };

    return (
        <>
            <GeneratorPage
                title={t("title")}
                description={t("description")}
                icon={<User className="h-4 w-4" />}
                category={CATEGORIES.personal}
                generatedData={generated.length ? generated.map((n) => n.fullName).join("\n") : null}
                isGenerating={isGenerating}
                onGenerate={handleGenerate}
                formConfig={formConfig}
                resultsRenderer={customResultsRenderer}
                faqs={t.raw("faqs")}
                similarGenerators={[]}
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block mb-1 font-medium">{t("form.gender")}</label>
                        {control ? (
                            <Controller
                                name="gender"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        <option value="any">{t("form.genderOptions.any")}</option>
                                        <option value="male">{t("form.genderOptions.male")}</option>
                                        <option value="female">{t("form.genderOptions.female")}</option>
                                    </Select>
                                )}
                            />
                        ) : (
                            <Select
                                value={fallbackForm.gender}
                                onChange={(e) => setFallbackForm(prev => ({ ...prev, gender: e.target.value }))}
                            >
                                <option value="any">{t("form.genderOptions.any")}</option>
                                <option value="male">{t("form.genderOptions.male")}</option>
                                <option value="female">{t("form.genderOptions.female")}</option>
                            </Select>
                        )}
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.nameType")}</label>
                        {control ? (
                            <Controller
                                name="nameType"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        <option value="full">{t("form.nameTypeOptions.full")}</option>
                                        <option value="first">{t("form.nameTypeOptions.first")}</option>
                                        <option value="last">{t("form.nameTypeOptions.last")}</option>
                                    </Select>
                                )}
                            />
                        ) : (
                            <Select
                                value={fallbackForm.nameType}
                                onChange={(e) => setFallbackForm(prev => ({ ...prev, nameType: e.target.value }))}
                            >
                                <option value="full">{t("form.nameTypeOptions.full")}</option>
                                <option value="first">{t("form.nameTypeOptions.first")}</option>
                                <option value="last">{t("form.nameTypeOptions.last")}</option>
                            </Select>
                        )}
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.nameset")}</label>
                        {control ? (
                            <Controller
                                name="nameset"
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    >
                                        {namesetOptions.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                        ))}
                                    </Select>
                                )}
                            />
                        ) : (
                            <Select
                                value={fallbackForm.nameset}
                                onChange={(e) => setFallbackForm(prev => ({ ...prev, nameset: e.target.value }))}
                            >
                                {namesetOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Select>
                        )}
                    </div>
                    <div>
                        <label className="block mb-1 font-medium">{t("form.count")}</label>
                        {control ? (
                            <Controller
                                name="count"
                                control={control}
                                rules={{
                                    required: true,
                                    min: 1,
                                    max: 10
                                }}
                                render={({ field }) => (
                                    <Input
                                        type="number"
                                        min={1}
                                        max={10}
                                        value={field.value}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                        placeholder={t("form.countPlaceholder")}
                                    />
                                )}
                            />
                        ) : (
                            <Input
                                type="number"
                                min={1}
                                max={10}
                                value={fallbackForm.count}
                                onChange={(e) => setFallbackForm(prev => ({ ...prev, count: Number(e.target.value) }))}
                                placeholder={t("form.countPlaceholder")}
                            />
                        )}
                    </div>
                </div>
            </GeneratorPage>

            <SchemaScript route={Routes.names} locale={locale} faqItems={t.raw("faqs")} />
        </>
    );
}